import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import { useBookingChannel } from '../../hooks/useBookingChannel';
import { useRoute } from '../../hooks/useRoute';
import LiveMap from '../LiveMap';
import RouteMap from './RouteMap';
import { SearchStatusPanel } from './SearchStatusPanel';
import { DriverProfileCard } from './DriverProfileCard';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { MapPin, Phone, Clock, Car, Star, ArrowLeft, Wifi, WifiOff, Navigation, UserRound, IndianRupee, Loader2 } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import EmergencySOSButton from '../EmergencySOSButton';

const SEARCH_STATUSES = ['searching', 'no_drivers'];

const ActiveBooking = () => {
  const navigate = useNavigate();
  const { activeBooking, updateBookingStatus, addRating, retrySearch, refreshActiveBooking, payForBooking, choosePayCash, confirmCashPaid } = useBooking();
  const { user } = useAuth();
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [choosingCash, setChoosingCash] = useState(false);
  const [cashAck, setCashAck] = useState(false);

  // Real-time location + status channel
  const { lastLocation, lastStatus, connected } = useBookingChannel(activeBooking?.id);

  // The instant the driver (or backend) pushes a status change over the socket,
  // pull the fresh booking instead of waiting for the next poll tick — this is
  // what keeps "Trip Completed" / "Cancelled" from lagging behind on screen.
  useEffect(() => {
    if (!lastStatus) return;
    refreshActiveBooking();
  }, [lastStatus, refreshActiveBooking]);
  const pickupPoint = activeBooking?.pickupLat != null ? { lat: activeBooking.pickupLat, lng: activeBooking.pickupLng } : null;
  const dropPoint = activeBooking?.dropLat != null ? { lat: activeBooking.dropLat, lng: activeBooking.dropLng } : null;
  const isSearchPhase = SEARCH_STATUSES.includes(activeBooking?.status);
  const { route } = useRoute(isSearchPhase ? pickupPoint : null, isSearchPhase ? dropPoint : null);

  useEffect(() => {
    // Status progression is now driven by the driver-side via the backend.
    // BookingContext polls /api/bookings/active and updates activeBooking automatically.
    // We only surface status-change toasts here.
    if (!activeBooking) return;
    if (activeBooking.status === 'assigned' && !activeBooking._toasted_assigned) {
      activeBooking._toasted_assigned = true;
      toast({ title: 'Driver Assigned', description: 'A driver has accepted your booking' });
    } else if (activeBooking.status === 'no_drivers' && !activeBooking._toasted_no_drivers) {
      activeBooking._toasted_no_drivers = true;
      toast({ title: 'No drivers available', description: 'Please try again — the next search covers 10 km', variant: 'destructive' });
    } else if (activeBooking.status === 'arriving' && !activeBooking._toasted_arriving) {
      activeBooking._toasted_arriving = true;
      toast({ title: 'Driver on the way', description: 'Your driver is arriving soon' });
    } else if (activeBooking.status === 'started' && !activeBooking._toasted_started) {
      activeBooking._toasted_started = true;
      toast({ title: 'Trip Started', description: 'Your hourly ride has begun' });
    } else if (activeBooking.status === 'completed' && !activeBooking._toasted_completed) {
      activeBooking._toasted_completed = true;
      toast({ title: 'Trip Completed', description: 'Please rate your driver' });
      setShowRatingModal(true);
    }
  }, [activeBooking?.status, activeBooking]);

  const handleCompleteTrip = async () => {
    // Drivers complete trips on their side; this is a fallback exit for the user.
    setShowRatingModal(true);
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast({
        title: 'Select Rating',
        description: 'Please select a rating',
        variant: 'destructive'
      });
      return;
    }
    const needsCashAck = activeBooking.paymentMethod === 'cash' && !activeBooking.cashConfirmedByUser;
    if (needsCashAck && !cashAck) {
      toast({
        title: 'Confirm Cash Payment',
        description: 'Please confirm you paid your driver in cash before submitting',
        variant: 'destructive',
      });
      return;
    }
    if (needsCashAck) {
      try {
        await confirmCashPaid(activeBooking.id);
      } catch { /* non-blocking — still let the rating go through */ }
    }

    addRating(activeBooking.id, rating, review);
    toast({
      title: 'Thank you!',
      description: 'Your feedback has been submitted'
    });
    setShowRatingModal(false);
    navigate('/user/dashboard', { replace: true });
  };

  const handlePayCash = async () => {
    setChoosingCash(true);
    try {
      await choosePayCash(activeBooking.id);
      toast({ title: 'Cash Payment Selected', description: 'Pay your driver directly once the trip ends' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: 'Could not select cash payment', description: typeof detail === 'string' ? detail : 'Please try again', variant: 'destructive' });
    } finally {
      setChoosingCash(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setBusy(true);
    try {
      const cancelled = await updateBookingStatus(activeBooking.id, 'cancelled');
      const refunded = cancelled?.paymentStatus === 'refunded';
      toast({
        title: 'Booking Cancelled',
        description: refunded
          ? `Refund of ₹${activeBooking.amount} initiated. It will reflect in 5-7 business days.`
          : 'Your booking has been cancelled'
      });
      navigate('/user/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Cancel Failed',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRetrySearch = async () => {
    setBusy(true);
    try {
      const updated = await retrySearch(activeBooking.id);
      toast({
        title: updated.status === 'searching' ? 'Searching again' : 'Still no drivers',
        description: updated.status === 'searching'
          ? `Now notifying drivers within ${updated.searchRadiusKm} km of your pickup`
          : 'No drivers are online within 10 km right now. Please try again shortly.',
        variant: updated.status === 'searching' ? undefined : 'destructive',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: 'Could not restart search', description: typeof detail === 'string' ? detail : 'Please try again', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handlePayNow = async () => {
    setPaying(true);
    try {
      await payForBooking(activeBooking.id, user);
      toast({ title: 'Payment Successful', description: 'Your trip is confirmed!' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      if (detail === 'Payment cancelled') {
        toast({ title: 'Payment Cancelled', description: 'Your driver is still assigned — pay whenever you are ready.' });
      } else {
        toast({ title: 'Payment Failed', description: typeof detail === 'string' ? detail : 'Please try again', variant: 'destructive' });
      }
    } finally {
      setPaying(false);
    }
  };

  if (!activeBooking) return null;

  const getStatusInfo = () => {
    switch (activeBooking.status) {
      case 'assigned':
        return {
          title: 'Driver Assigned',
          description: 'Your driver has been assigned',
          color: 'bg-green-500'
        };
      case 'arriving':
        return {
          title: 'Driver Arriving',
          description: 'Your driver is on the way',
          color: 'bg-accent-dark'
        };
      case 'started':
        return {
          title: 'Trip in Progress',
          description: 'Your hourly ride is active',
          color: 'bg-primary'
        };
      case 'completed':
        return {
          title: 'Trip Completed',
          description: 'Thanks for riding with BookMyDriver',
          color: 'bg-primary-dark'
        };
      case 'cancelled':
        return {
          title: 'Booking Cancelled',
          description: 'This booking has been cancelled',
          color: 'bg-red-500'
        };
      default:
        return {
          title: 'Booking Active',
          description: 'Processing your request',
          color: 'bg-primary/70'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const bookingInFlight = activeBooking && !['completed', 'cancelled'].includes(activeBooking.status);
  const canCancel = ['assigned', 'arriving'].includes(activeBooking.status);
  const isEnded = ['completed', 'cancelled'].includes(activeBooking.status);
  const needsPayment = activeBooking.paymentStatus === 'pending' && activeBooking.paymentMethod !== 'cash'
    && ['assigned', 'arriving'].includes(activeBooking.status);

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <header className="bg-white border-b p-4">
        <div className="container mx-auto">
          <div className="flex items-center gap-4">
            {!bookingInFlight && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/user/dashboard')}
                data-testid="active-booking-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <BrandLogo variant="compact" className="h-8 w-auto" />
            {bookingInFlight && (
              <span className="ml-auto text-xs text-muted-foreground">Cancel below to leave</span>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Status Banner */}
        {isSearchPhase ? (
          <SearchStatusPanel booking={activeBooking} onRetry={handleRetrySearch} onCancel={handleCancelBooking} busy={busy} />
        ) : (
          <Card className={`${statusInfo.color} text-white p-6 mb-6`}>
            <h2 className="text-2xl font-bold mb-2" data-testid="active-booking-status">{statusInfo.title}</h2>
            <p className="text-white/90">{statusInfo.description}</p>
          </Card>
        )}

        {/* Map */}
        <Card className="mb-6 overflow-hidden relative">
          {/* Connection badge */}
          <div className="absolute top-3 left-3 z-[500]">
            {connected ? (
              <span className="flex items-center gap-1 bg-green-600 text-white text-xs px-2 py-1 rounded-full shadow" data-testid="ws-connected-badge">
                <Wifi className="w-3 h-3" /> Live
              </span>
            ) : (
              <span className="flex items-center gap-1 bg-primary/70 text-white text-xs px-2 py-1 rounded-full shadow">
                <WifiOff className="w-3 h-3" /> Connecting…
              </span>
            )}
          </div>
          {isSearchPhase && pickupPoint ? (
            <RouteMap pickup={pickupPoint} drop={dropPoint} route={route} height={288} />
          ) : lastLocation ? (
            <div data-testid="live-location-display">
              <LiveMap lat={lastLocation.lat} lng={lastLocation.lng} height={288} />
              <div className="px-4 py-2 bg-background text-xs text-muted-foreground flex items-center justify-between">
                <span>Driver is moving</span>
                <span className="font-mono">
                  {lastLocation.lat.toFixed(5)}, {lastLocation.lng.toFixed(5)}
                </span>
              </div>
            </div>
          ) : (
            <div className="relative h-72 bg-gradient-to-br from-muted to-background flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {activeBooking?.status === 'searching' ? 'Waiting for driver…' : 'Waiting for driver location…'}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Booking Details */}
        <Card className="p-6 mb-6">
          <h3 className="font-semibold text-lg mb-4">Booking Details</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Pickup Location</p>
                <p className="font-medium">{activeBooking.pickupLocation}</p>
              </div>
            </div>
            {activeBooking.dropLocation && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Drop Location</p>
                  <p className="font-medium">{activeBooking.dropLocation}</p>
                </div>
              </div>
            )}
            {activeBooking.carTypeName && (
              <div className="flex items-center gap-3">
                <Car className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Car Type</p>
                  <p className="font-medium">{activeBooking.carTypeName}</p>
                </div>
              </div>
            )}
            {activeBooking.routeDistanceKm != null && (
              <div className="flex items-center gap-3">
                <Navigation className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Route</p>
                  <p className="font-medium" data-testid="booking-route-info">
                    {activeBooking.routeDistanceKm} km · ~{activeBooking.routeDurationMin} min drive
                  </p>
                </div>
              </div>
            )}
            {activeBooking.femaleDriverOnly && (
              <div className="flex items-center gap-3">
                <UserRound className="w-5 h-5 text-pink-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Driver Preference</p>
                  <p className="font-medium" data-testid="booking-female-only">Female drivers only</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="font-medium">{activeBooking.hours} Hour(s)</p>
              </div>
            </div>
            {activeBooking.scheduledDate && (
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 text-muted-foreground">📅</span>
                <div>
                  <p className="text-sm text-muted-foreground">Scheduled For</p>
                  <p className="font-medium">
                    {new Date(activeBooking.scheduledDate + ' ' + activeBooking.scheduledTime).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="text-2xl">₹</span>
              <div>
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="font-medium text-xl">₹{activeBooking.amount}</p>
                {activeBooking.discount > 0 && (
                  <p className="text-xs text-green-600">Saved ₹{activeBooking.discount}</p>
                )}
                {activeBooking.lateNightCharge > 0 && (
                  <p className="text-xs text-amber-700" data-testid="active-booking-late-night-charge">
                    Includes ₹{activeBooking.lateNightCharge} late night charge
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Driver Details */}
        {activeBooking.driverName && <DriverProfileCard booking={activeBooking} />}

        {/* Payment required — shown the moment a driver accepts, before trip PIN/tracking unlock */}
        {needsPayment && (
          <Card className="p-6 mb-6 border-2 border-primary bg-primary/5 text-center" data-testid="payment-required-card">
            <IndianRupee className="w-10 h-10 mx-auto mb-2 text-primary" />
            <h3 className="text-xl font-bold mb-1">Complete Payment to Confirm</h3>
            <p className="text-muted-foreground mb-4">
              {activeBooking.driverName} has been assigned. Pay ₹{activeBooking.amount} now, or pay your driver in cash,
              to get your trip PIN and start tracking.
            </p>
            <div className="space-y-2">
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary-dark text-lg"
                onClick={handlePayNow}
                disabled={paying || choosingCash}
                data-testid="pay-now-btn"
              >
                {paying ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing…</> : `Pay ₹${activeBooking.amount} Now`}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full"
                onClick={handlePayCash}
                disabled={paying || choosingCash}
                data-testid="pay-cash-btn"
              >
                {choosingCash ? 'Selecting…' : 'Pay Cash to Driver'}
              </Button>
            </div>
          </Card>
        )}

        {activeBooking.paymentMethod === 'cash' && activeBooking.paymentStatus === 'pending' && (
          <p className="text-center text-sm text-muted-foreground mb-4" data-testid="cash-pending-note">
            Paying ₹{activeBooking.amount} by cash — hand it to your driver at the end of the trip.
          </p>
        )}

        {/* Trip PIN — share with your driver so they can start the trip */}
        {!needsPayment && activeBooking.startPin && ['assigned', 'arriving'].includes(activeBooking.status) && (
          <Card className="p-6 mb-6 border-2 border-dashed border-border bg-background text-center" data-testid="trip-pin-card">
            <p className="text-sm text-muted-foreground mb-2">Share this PIN with your driver to start the trip</p>
            <p className="text-4xl font-bold font-mono tracking-widest" data-testid="trip-pin-value">{activeBooking.startPin}</p>
          </Card>
        )}

        {/* Emergency SOS Button */}
        <div className="mb-6">
          <EmergencySOSButton userType="user" />
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {activeBooking.status === 'started' && (
            <Button
              size="lg"
              className="w-full bg-green-600 hover:bg-green-700 text-lg"
              onClick={handleCompleteTrip}
            >
              Complete Trip
            </Button>
          )}
          {canCancel && (
            <Button
              size="lg"
              variant="destructive"
              data-testid="cancel-booking-btn"
              className="w-full text-lg"
              onClick={handleCancelBooking}
              disabled={busy}
            >
              Cancel Booking
            </Button>
          )}
          {isEnded && (
            <Button
              size="lg"
              className="w-full text-lg bg-primary hover:bg-primary-dark"
              data-testid="active-booking-done-btn"
              onClick={() => navigate('/user/dashboard', { replace: true })}
            >
              Back to Dashboard
            </Button>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Your Experience</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {activeBooking.paymentMethod === 'cash' && !activeBooking.cashConfirmedByUser && (
              <label className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3 cursor-pointer"
                data-testid="cash-confirm-checkbox-label">
                <input
                  type="checkbox"
                  checked={cashAck}
                  onChange={(e) => setCashAck(e.target.checked)}
                  className="mt-1"
                  data-testid="cash-confirm-checkbox"
                />
                <span className="text-sm text-green-900">
                  I confirm I paid <strong>₹{activeBooking.amount}</strong> in cash to my driver for this trip.
                </span>
              </label>
            )}
            <div>
              <Label className="mb-2 block">Rating</Label>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        star <= rating
                          ? 'text-yellow-500 fill-current'
                          : 'text-muted-foreground/50'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="review">Review (Optional)</Label>
              <Textarea
                id="review"
                placeholder="Share your experience..."
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={4}
              />
            </div>
            <Button
              className="w-full bg-primary hover:bg-primary-dark"
              onClick={handleSubmitRating}
              data-testid="submit-rating-btn"
            >
              Submit Rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiveBooking;
